import React from "react";
import WithoutReact from "../examples/WithoutReact";
import ApiLink from "../../ui/ApiLink";
import { Tag } from "@dossier/salvia-ui";

const miniGuides =  {
  name: "Mini-Guides",
  content: (
    <>
      <p>
        These mini-guides explain how to do different things with minibeak. Most
        of them are also covered by the{" "}
        <a href="#React example code">example code</a> above.
      </p>
    </>
  ),
  children: [
    {
      name: "Fetching resources",
      content: (
        <>
          <p>
            You can use the <ApiLink name="useProFile" /> hook to fetch
            resources from ProFile. For example, look at how it is used in the{" "}
            <a href="#React example code">example code</a> above. For non-react
            usage, see{" "}
            <code>
              <a href="#Without React">Without React</a>
            </code>
            .
          </p>
        </>
      ),
    },
    {
      name: "Waiting for data to load",
      content: (
        <p>
          <ApiLink name="useProFile" /> returns a{" "}
          <ApiLink name="MinibeakHookResult" /> that contains a bunch of
          different useful stuff. You can check the value of{" "}
          <code>.isReady</code> or <code>.isFetching</code> to see if the
          requested resource is available, and if it is being fetched. Or you
          can just check if <code>.data</code> or <code>.error</code> is
          present.
        </p>
      ),
    },
    {
      name: "Delaying a call with useProFile",
      content: (
        <p>
          Some times you don't have all the data needed to make a call with{" "}
          <ApiLink name="useProFile"/>. For example, maybe you need to wait for
          another call to finish first. You can prevent{" "}
          <ApiLink name="useProFile"/> from actually making any call by passing{" "}
          <code>path: undefined</code> or <code>shouldExecute: false</code> to <ApiLink name="MinibeakConfig"/> until the data become available.
        </p>
      ),
    },
    {
      name: "Updating, creating and deleting resources",
      content: (
        <>
          <p>
            You can make calls to ProFile by calling{" "}
            <ApiLink name="callProFile" /> directly. There is no need to make
            action creators and bind them, like in Kolibri. See how{" "}
            <ApiLink name="callProFile" /> is used in the{" "}
            <a href="#React example code">example code</a> above.
          </p>
        </>
      ),
    },
    {
      name: "Error handling",
      content: (
        <>
          <p>
            Backend calls may fail for several reasons. The request itself may
            fail, or it may have the wrong status code (for example, if
            authentication failed or access was denied).
          </p>

          <p>
            <strong>
              <code>useProFile</code>:
            </strong>
          </p>

          <p>
            <ApiLink name="useProFile" /> returns a{" "}
            <ApiLink name="MinibeakHookResult" /> that has a field called{" "}
            <code>.error</code> when there are any errors during the request.
            You can inspect this error to find out what failed.{" "}
            <ApiLink name="ResponseNotOkError" /> provides the{" "}
            <code>.request</code> property. Errors shuold be responded to in
            some way. See <a href="#React example code">the example code</a>{" "}
            above.
          </p>

          <p>
            <strong>
              <code>callProFile</code>:
            </strong>
          </p>

          <p>
            <ApiLink name="callProFile" /> returns a promise. When there is an
            error, this fails with the given error. You can see examples of this
            in the try-catch blocks in the event handlers in{" "}
            <a href="#React example code">the example code</a>.
          </p>
        </>
      ),
    },

    {
      name: "Refreshing stale resources",
      content: (
        <>
          <p>
            Minibeak does not have a central resource store. This means that
            when a call changes some resource that was populated elsewhere, that{" "}
            <ApiLink name="useProFile" /> result must be manually refreshed. You
            can see examples of this in the event handlers in the{" "}
            <a href="#React example code">example code</a>.
          </p>

          <p>
            <ApiLink name="useProFile" /> refreshes automatically when its
            configuration changes.
          </p>
        </>
      ),
    },
    {
      name: "Custom HTTP parameters",
      content: (
        <>
          <p>
            <ApiLink name="useProFile" /> and <ApiLink name="callProFile" />{" "}
            support a lot of different configuration parameters. See{" "}
            <ApiLink name="MinibeakConfig" /> for more.
          </p>

          <p>
            It also includes all of the options supported by the
            <a href="https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API">
              {" "}
              HTTP fetch API
            </a>
            .
          </p>
          <p>
            For example, to set a custom header, you can just set the{" "}
            <code>headers</code> field on the request config to an array of the
            headers you want.
          </p>
        </>
      ),
    },
    {
      name: "Caching",
      content: (
        <>
          <p>
            <Tag intent="danger" label="Experimental" /> Minibeak has built-in
            backend call caching. This can be used to cache the results of calls
            based on their config (until the browser is refreshed). This is
            useful when users are expected to keep revisiting expensive fetches.
          </p>

          <p>
            Caching can be enabled simply by setting <code>minibeakCache</code>{" "}
            to <code>true</code> in the <ApiLink name="MinibeakConfig" />.
          </p>

          <p>
            <code>minibeakCache</code> can also be set to a number of seconds,
            after which the cache will time out.
          </p>

          <p>
            It can also be set to a string <em>cache tag</em>, that can later be
            used with <ApiLink name="clearCallCache" /> to clear all caches with
            that tag.
          </p>

          <p>
            It can also be set to an array combining several cache tags and a
            timeout.
          </p>
        </>
      ),
    },
    {
      name: "Without React",
      content: <WithoutReact />,
    },
  ],
};

export default miniGuides;